import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MapPin, Trash2, Plus, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function AdminLocationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [affected, setAffected] = useState(0);
  const [term, setTerm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user || user.role !== "admin") return;
    try {
      const r = await api.get("/admin/location-blocklist");
      setItems(r.data.items || []);
      setAffected(r.data.affected_active_jobs || 0);
    } catch {
      toast.error("Failed to load blocklist");
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!term.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/location-blocklist", { term: term.trim(), reason: reason.trim() || undefined });
      toast.success(`Added · ${r.data.deactivated_jobs} jobs deactivated`);
      setTerm(""); setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setBusy(false); }
  };

  const remove = async (entry_id, t) => {
    if (!confirm(`Remove "${t}" from the blocklist? Jobs previously blocked will reactivate only when the crawler re-discovers them.`)) return;
    try {
      await api.delete(`/admin/location-blocklist/${entry_id}`);
      toast.success(`Removed "${t}"`);
      load();
    } catch {
      toast.error("Remove failed");
    }
  };

  const applyNow = async () => {
    try {
      const r = await api.post("/admin/location-blocklist/apply-now");
      toast.success(`Cleanup complete · ${r.data.deactivated_total} jobs deactivated`);
      load();
    } catch {
      toast.error("Apply failed");
    }
  };

  if (!user || user.role !== "admin") {
    return (
      <div className="container py-20 max-w-xl text-center" data-testid="admin-locations-locked">
        <h1 className="display-2 mb-4">Admin only</h1>
      </div>
    );
  }

  return (
    <div className="container py-10 max-w-3xl">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="admin-locations-back">
        <ArrowLeft className="h-4 w-4" /> Back to Admin Console
      </Link>
      <p className="eyebrow mb-1 inline-flex items-center gap-2"><MapPin className="h-3 w-3" /> Location filter</p>
      <h1 className="display-2 mb-2">{items.length} blocked terms</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Substring filter (case-insensitive). Adding a term immediately deactivates matching active jobs.
        {affected > 0 && (
          <span className="text-amber-700 dark:text-amber-400 ml-1">
            · {affected} jobs currently match the blocklist
          </span>
        )}
      </p>

      <section className="vital-card-static p-5 mb-6" data-testid="locations-add">
        <h3 className="font-bold mb-3 inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Add a term</h3>
        <div className="grid md:grid-cols-[1fr_2fr_auto] gap-3">
          <div>
            <Label className="text-xs">Term</Label>
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Indianapolis"
              data-testid="locations-add-term"
            />
          </div>
          <div>
            <Label className="text-xs">Reason (optional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="False-positive India match"
              data-testid="locations-add-reason"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={add} disabled={busy || !term.trim()} data-testid="locations-add-btn">Add & clean</Button>
          </div>
        </div>
      </section>

      <div className="flex justify-end mb-3">
        <Button variant="outline" size="sm" onClick={applyNow} data-testid="locations-apply-now">
          <Sparkles className="mr-2 h-4 w-4" /> Re-apply all to current jobs
        </Button>
      </div>

      <section className="overflow-x-auto rounded-lg border border-border" data-testid="locations-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
              <th className="py-2 px-3">Term</th>
              <th className="py-2 px-3">Reason</th>
              <th className="py-2 px-3">Added</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((it) => (
              <tr key={it.entry_id} data-testid={`location-row-${it.entry_id}`}>
                <td className="py-2 px-3 font-semibold">{it.term}</td>
                <td className="py-2 px-3 text-muted-foreground text-xs">{it.reason}</td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{new Date(it.created_at).toLocaleDateString()}</td>
                <td className="py-2 px-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(it.entry_id, it.term)} data-testid={`location-remove-${it.entry_id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="py-10 text-center text-muted-foreground" data-testid="locations-empty">No terms blocked yet.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <p className="text-xs text-muted-foreground mt-4 inline-flex items-center gap-1.5">
        <AlertTriangle className="h-3 w-3" /> Removing a term does NOT auto-reactivate previously deactivated jobs. They&apos;ll come back on the next crawl if they reappear at source.
      </p>
    </div>
  );
}
